// ---------------------------------------------------------------------------
// Storey → floor mapping for the "stock from CAD" panel.
//
// The take-off's storeys and the site's floor catalog (`ISiteLocations`) are
// two independent lists with no shared id — storeys come from the IFC model,
// floors from the (mocked, DAITEC-owned) area catalog. Spec §3 fixes the
// pairing with one hardcoded anchor: CAD storey "110" ↔ the project's HIGHEST
// floor, which is `floors[0]` because the catalog is ordered top-down (the
// same order the Locations tab renders). From that anchor both lists walk
// downward in lockstep — storeys sorted by descending elevation against
// `floors` in catalog order — mapping every storey the catalog has room for.
// Storeys ABOVE the anchor (parapet, roof structure) or past the end of
// `floors` are left unmapped rather than guessed at.
//
// Pure function — no React, no store; unit-tested in isolation.
// ---------------------------------------------------------------------------

import type { ISiteFloor } from '@interfaces/IScheduler';
import type { ITakeoffStorey } from '@interfaces/ICadTakeoff';

/** The storey name spec §3 pins to the project's highest floor — the fixed
 *  point that lines up the take-off's storeys with the site's
 *  (independently-authored) floor catalog. Hardcoded for the preview; the real
 *  version is a per-site, admin-editable storey ↔ floorId table seeded by it. */
export const ANCHOR_STOREY_NAME = '110';

/**
 * Maps each take-off storey's `localId` to a floor `id` from the site catalog.
 * Storeys are sorted by elevation descending (regardless of input order) and
 * anchored to `floors[0]` at the `ANCHOR_STOREY_NAME` storey; walking both
 * lists downward from there maps as many storeys as `floors` has room for.
 * Storeys with a null elevation, above the anchor, or past the end of
 * `floors` are left out of the returned map. Returns an empty map when no
 * storey is named `ANCHOR_STOREY_NAME`.
 */
export function mapStoreysToFloors(
  storeys: ITakeoffStorey[],
  floors: ISiteFloor[],
): Map<number, string> {
  const sorted = storeys
    .filter((s): s is ITakeoffStorey & { elevation: number } => s.elevation !== null)
    .sort((a, b) => b.elevation - a.elevation);
  const anchor = sorted.findIndex((s) => s.name === ANCHOR_STOREY_NAME);
  const out = new Map<number, string>();
  if (anchor < 0) return out;
  for (let i = 0; i < floors.length && anchor + i < sorted.length; i += 1) {
    out.set(sorted[anchor + i]!.localId, floors[i]!.id);
  }
  return out;
}
